import React, { useState, useCallback } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Box,
    Stack,
    Typography,
    Alert,
    Switch,
    FormControlLabel,
    Paper,
} from '@mui/material';
import {
    Download as DownloadIcon,
    Upload as UploadIcon,
    Close as CloseIcon,
    EmailOutlined as EmailOutlinedIcon,
} from '@mui/icons-material';

import { useJoinUsersBulk } from '@/hooks/users.hook';
import { successToast, errorToast } from '@/common/toast';

/**
 * 엑셀 업로드 모달 컴포넌트
 * 
 * 엑셀 템플릿 다운로드 및 교육생 대량 등록을 위한 모달입니다.
 * 
 * @param {Object} props
 * @param {boolean} props.open - 모달 열림 상태
 * @param {Function} props.onClose - 모달 닫기 핸들러
 * @param {number} props.groupId - 그룹 ID
 * @param {Function} props.onSuccess - 업로드 성공 후 콜백
 */
const ExcelUploadModal = ({
    open,
    onClose,
    groupId,
    onSuccess,
}) => {
    const [selectedFile, setSelectedFile] = useState(null);
    const [isUploading, setIsUploading] = useState(false);
    // 대량 등록 시 메일 전송 여부
    const [sendWelcomeEmail, setSendWelcomeEmail] = useState(false);

    // 교육생 대량 등록 mutation
    const joinUsersBulkMutation = useJoinUsersBulk();

    /**
     * 엑셀 템플릿 다운로드 핸들러
     */
    const handleDownloadTemplate = useCallback(() => {
        try {
            // public 폴더의 파일 경로
            const templateUrl = '/source/userFile.xlsx';

            // 링크 생성하여 다운로드
            const link = document.createElement('a');
            link.href = templateUrl;
            link.download = 'userFile.xlsx';
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);

            successToast('엑셀 템플릿이 다운로드되었습니다.');
        } catch (error) {
            errorToast('템플릿 다운로드에 실패했습니다.');
            console.error('템플릿 다운로드 오류:', error);
        }
    }, []);

    /**
     * 파일 선택 핸들러
     */
    const handleFileSelect = useCallback((event) => {
        const file = event.target.files?.[0];
        if (file) {
            // 엑셀 파일 확장자 검증
            const validExtensions = ['.xlsx', '.xls'];
            const fileExtension = file.name.substring(file.name.lastIndexOf('.')).toLowerCase();

            if (!validExtensions.includes(fileExtension)) {
                errorToast('엑셀 파일(.xlsx, .xls)만 업로드 가능합니다.');
                return;
            }

            setSelectedFile(file);
        }
    }, []);

    /**
     * 파일 업로드 핸들러
     */
    const handleUpload = useCallback(async () => {
        if (!selectedFile) {
            errorToast('파일을 선택해주세요.');
            return;
        }

        if (!groupId) {
            errorToast('그룹 ID가 없습니다.');
            return;
        }

        setIsUploading(true);

        try {
            const response = await joinUsersBulkMutation.mutateAsync({
                groupId: parseInt(groupId),
                file: selectedFile,
                sendWelcomeEmail: sendWelcomeEmail,
            });
            const { msg, data } = response;
            successToast(`${msg}${data && `\nJOB ID: ${data}`}`);
            setSelectedFile(null);
            onSuccess?.();
            onClose();
        } catch (error) {
            errorToast(`교육생 대량 등록에 실패했습니다. - ${error.response?.data?.msg}`);
        } finally {
            setIsUploading(false);
        }
    }, [selectedFile, groupId, joinUsersBulkMutation, onSuccess, onClose]);

    /**
     * 모달 닫기 핸들러
     */
    const handleClose = useCallback(() => {
        if (!isUploading) {
            setSelectedFile(null);
            onClose();
        }
    }, [isUploading, onClose]);

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            maxWidth="sm"
            fullWidth
        >
            <DialogTitle>
                엑셀 교육생 대량 등록
            </DialogTitle>
            <DialogContent>
                <Stack spacing={3} sx={{ mt: 1 }}>
                    {/* 안내 메시지 */}
                    <Alert severity="info">
                        <Typography variant="body2">
                            1. 엑셀 템플릿을 다운로드하세요.
                            <br />
                            2. 템플릿에 교육생 정보를 입력하세요.
                            <br />
                            3. 파일을 선택하여 업로드하세요.
                        </Typography>
                    </Alert>

                    {/* 템플릿 다운로드 버튼 */}
                    <Box>
                        <Button
                            variant="outlined"
                            fullWidth
                            startIcon={<DownloadIcon />}
                            onClick={handleDownloadTemplate}
                            sx={{ mb: 2 }}
                        >
                            엑셀 템플릿 다운로드
                        </Button>
                    </Box>

                    {/* 파일 선택 */}
                    <Box>
                        <Typography variant="body2" gutterBottom sx={{ mb: 1 }}>
                            엑셀 파일 선택
                        </Typography>
                        <input
                            type="file"
                            accept=".xlsx,.xls"
                            onChange={handleFileSelect}
                            disabled={isUploading}
                            style={{ display: 'none' }}
                            id="excel-file-input"
                        />
                        <label htmlFor="excel-file-input">
                            <Button
                                variant="outlined"
                                component="span"
                                fullWidth
                                startIcon={<UploadIcon />}
                                disabled={isUploading}
                                sx={{ mb: 1 }}
                            >
                                파일 선택
                            </Button>
                        </label>
                        {selectedFile && (
                            <Box sx={{ mt: 1, p: 1, bgcolor: '#f5f5f5', borderRadius: 1 }}>
                                <Stack direction="row" justifyContent="space-between" alignItems="center">
                                    <Typography variant="body2">
                                        선택된 파일: {selectedFile.name}
                                    </Typography>
                                    <Button
                                        size="small"
                                        onClick={() => setSelectedFile(null)}
                                        disabled={isUploading}
                                        startIcon={<CloseIcon />}
                                    >
                                        제거
                                    </Button>
                                </Stack>
                            </Box>
                        )}
                    </Box>

                    {/* 메일 전송 */}
                    <Paper
                        variant="outlined"
                        sx={{
                            p: 2,
                            borderRadius: 1,
                            bgcolor: 'grey.50',
                            borderColor: 'divider',
                        }}
                    >
                        <FormControlLabel
                            sx={{
                                m: 0,
                                ml: 0,
                                mr: 0,
                                width: '100%',
                                alignItems: 'flex-start',
                                justifyContent: 'space-between',
                                gap: 1,
                            }}
                            label={
                                <Stack direction="row" spacing={1.25} alignItems="flex-start" sx={{ pr: 1 }}>
                                    <EmailOutlinedIcon
                                        sx={{ color: 'primary.main', mt: 0.15, flexShrink: 0 }}
                                        fontSize="small"
                                    />
                                    <Box>
                                        <Typography variant="subtitle2" fontWeight={600} component="div">
                                            메일 전송 방식
                                        </Typography>
                                        <Typography
                                            variant="caption"
                                            color="text.secondary"
                                            component="div"
                                            sx={{ display: 'block', mt: 0.25 }}
                                        >
                                            {sendWelcomeEmail ? '교육생 개별 발송' : '관리자 일괄 발송'}
                                        </Typography>
                                    </Box>
                                </Stack>
                            }
                            labelPlacement="start"
                            control={
                                <Switch
                                    checked={sendWelcomeEmail}
                                    onChange={(e) => setSendWelcomeEmail(e.target.checked)}
                                    disabled={isUploading}
                                    color="primary"
                                />
                            }
                        />
                        <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{
                                mt: 1.5,
                                pl: 0.25,
                                lineHeight: 1.55,
                                borderTop: 1,
                                borderColor: 'divider',
                                pt: 1.5,
                            }}
                        >
                            {sendWelcomeEmail
                                ? '가입되는 교육생 각자에게 계정 정보를 메일로 전송합니다.'
                                : '관리자에게 한 번에 모든 계정 정보를 메일로 전송합니다.'}
                        </Typography>
                    </Paper>
                </Stack>
            </DialogContent>
            <DialogActions>
                <Button onClick={handleClose} disabled={isUploading}>
                    취소
                </Button>
                <Button
                    variant="contained"
                    onClick={handleUpload}
                    disabled={!selectedFile || isUploading}
                    startIcon={<UploadIcon />}
                >
                    {isUploading ? '업로드 중...' : '업로드'}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default ExcelUploadModal;
